import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { toast } from '@apps/web/lib/app_notifications';
import { useMode } from '@apps/web/lib/mode_store';
import {
    batchUpdateRecommendationCaregiverSelections,
    type RecommendationUnitType,
    useOutreachCaregiverSelections,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OutreachCaregiverSelectionRead } from '@libraries/typescript/api_client';

export type OutreachV3SelectionState = {
    readonly isReady: boolean;
    readonly stagedCaregiverIds: ReadonlySet<string>;
    /** Staged shift ids per caregiver, across the whole outreach. */
    readonly selectionCoverageByCaregiverId: ReadonlyMap<
        string,
        ReadonlySet<string>
    >;
    readonly persistSelectionDelta: (delta: {
        readonly addedIds: ReadonlyArray<string>;
        readonly removedIds: ReadonlyArray<string>;
    }) => void;
    /** Stage or unstage one caregiver for one member shift of a group unit. */
    readonly persistShiftSelection: (change: {
        readonly caregiverId: string;
        readonly shiftId: string;
        readonly selected: boolean;
    }) => void;
};

const EMPTY_SELECTIONS: ReadonlyArray<OutreachCaregiverSelectionRead> = [];
type ShiftOverride = {
    readonly caregiverId: string;
    readonly shiftId: string;
    readonly selected: boolean;
};

const EMPTY_OVERRIDES: ReadonlyMap<string, boolean> = new Map();
const EMPTY_SHIFT_OVERRIDES: ReadonlyMap<string, ShiftOverride> = new Map();
const NO_PENDING_PICKS: ReadonlyArray<ShiftOverride> = [];
const NO_UNIT_SHIFT_IDS: ReadonlyArray<string> = [];
const SELECTION_EVENT_THROTTLE_MS = 3000;

function toastSelectionSaveFailed() {
    toast.error('Could not save caregiver selection changes.', {
        persistNotification: false,
    });
}

/**
 * Returns the same map when nothing was dropped, so state setters can skip
 * the update.
 */
function dropOverrides<Override>(
    overrides: ReadonlyMap<string, Override>,
    shouldDrop: (key: string, override: Override) => boolean,
): ReadonlyMap<string, Override> {
    const remaining = new Map(
        [...overrides].filter(([key, override]) => !shouldDrop(key, override)),
    );
    return remaining.size === overrides.size ? overrides : remaining;
}

/**
 * Staged shift ids per caregiver: the server's staged rows with the
 * unconfirmed picks laid over them in order. A caregiver with nothing staged
 * has no entry.
 */
function stagedShiftIdsByCaregiver(
    selections: ReadonlyArray<OutreachCaregiverSelectionRead>,
    pendingPicks: ReadonlyArray<ShiftOverride>,
): ReadonlyMap<string, ReadonlySet<string>> {
    const coverage = new Map<string, Set<string>>();
    const stage = (caregiverId: string, shiftId: string) => {
        const shiftIds = coverage.get(caregiverId);
        if (shiftIds) {
            shiftIds.add(shiftId);
        } else {
            coverage.set(caregiverId, new Set([shiftId]));
        }
    };
    for (const selection of selections) {
        if (selection.status === 'staged') {
            stage(selection.caregiver_id, selection.shift_id);
        }
    }
    for (const { caregiverId, shiftId, selected } of pendingPicks) {
        if (selected) {
            stage(caregiverId, shiftId);
            continue;
        }
        const shiftIds = coverage.get(caregiverId);
        shiftIds?.delete(shiftId);
        if (shiftIds?.size === 0) {
            coverage.delete(caregiverId);
        }
    }
    return coverage;
}

function selectionMatchesUnit(
    selection: OutreachCaregiverSelectionRead,
    unitType: RecommendationUnitType,
    unitId: string,
) {
    return unitType === 'shift_group'
        ? selection.group_id === unitId
        : selection.shift_id === unitId;
}

/**
 * The v3-born caregiver table's checkbox state for one recommendation unit.
 *
 * The server's selection rows are the truth and the checkbox is derived from
 * them, so a repaint driven by the runner or another coordinator lands on an
 * open page. `stagedOverrides` holds only the deltas a write has not been
 * confirmed for yet: each one is dropped the moment fresh rows agree with it,
 * so a slow response can never invert a newer server state, and a failed
 * write rolls back exactly the ids that failed.
 *
 * Unchecking dismisses a suggestion the agent staged (the coordinator is
 * overruling Phoebe, which the agent must see) but withdraws one a
 * coordinator staged. `locallyStagedIdsRef` carries that signal for a row
 * this session just staged, because the server rows still describe the
 * pre-stage world until the refetch lands.
 */
export function useOutreachV3SelectionState({
    organizationId,
    outreachId,
    unitType,
    unitId,
    unitShiftIds = NO_UNIT_SHIFT_IDS,
    enabled,
    onDismissalsPersisted,
}: {
    readonly organizationId: string | null;
    readonly outreachId: string | null | undefined;
    readonly unitType: RecommendationUnitType;
    readonly unitId: string | null;
    /**
     * A group unit's member shifts. With them a whole-row pick shows on every
     * pill before the server confirms it; without them it shows only on the
     * row checkbox.
     */
    readonly unitShiftIds?: ReadonlyArray<string>;
    readonly enabled: boolean;
    readonly onDismissalsPersisted?: (
        caregiverIds: ReadonlyArray<string>,
    ) => void;
}): OutreachV3SelectionState {
    const mode = useMode();
    const { data, error, mutate } = useOutreachCaregiverSelections(
        organizationId,
        outreachId,
        { reason: 'inhouse-recommendations-table-v3-selections' },
        { enabled },
    );
    const [stagedOverrides, setStagedOverrides] =
        useState<ReadonlyMap<string, boolean>>(EMPTY_OVERRIDES);
    // The same unconfirmed-delta idea, one level finer: one entry per
    // caregiver and shift, for a coordinator picking single shifts of a group.
    const [shiftOverrides, setShiftOverrides] = useState(EMPTY_SHIFT_OVERRIDES);
    const locallyStagedIdsRef = useRef<Set<string>>(new Set());

    // Throttling belongs after the outreach-id filter: a listener-level
    // throttle coalesces every organization's events, so an event for
    // another outreach can swallow this one's trailing refetch.
    const throttledRefresh = useThrottledCallback(() => {
        void mutate();
    }, SELECTION_EVENT_THROTTLE_MS);
    useEffect(() => throttledRefresh.cancel, [outreachId, throttledRefresh]);
    const handleSelectionEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                event.resource.type === 'outreach' &&
                event.resource.id === outreachId
            ) {
                throttledRefresh();
            }
        },
        [outreachId, throttledRefresh],
    );
    // Selection rows change under two different signals and neither covers
    // the other: the REST selection routes take their own lock and publish
    // only the suggestion events, while a write through the outreach lock
    // (adding shifts propagates a group's dismissals onto them) publishes
    // only `outreach_updated`.
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        handleSelectionEvent,
        { organizationId },
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_removed',
        handleSelectionEvent,
        { organizationId },
    );
    useListenForOrganizationEvent('outreach_updated', handleSelectionEvent, {
        organizationId,
    });

    const unitSelections = useMemo(() => {
        if (data === undefined || unitId === null) {
            return EMPTY_SELECTIONS;
        }
        return data.selections.filter((selection) =>
            selectionMatchesUnit(selection, unitType, unitId),
        );
    }, [data, unitId, unitType]);

    const serverStagedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter((selection) => selection.status === 'staged')
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    // Routing input, not display: unchecking a row the agent staged
    // dismisses it, while one a coordinator staged is withdrawn. A group
    // unit holds one row per shift, so the decision reduces with OR across
    // the caregiver's rows — a per-caregiver map would let whichever row
    // came last in iteration order decide for all of them.
    const userStagedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter(
                        (selection) =>
                            selection.status === 'staged' &&
                            selection.status_changed_by === 'user',
                    )
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    const scopeKey = `${mode}:${outreachId ?? ''}:${unitType}:${unitId ?? ''}`;
    useEffect(() => {
        locallyStagedIdsRef.current = new Set();
        setStagedOverrides((previousOverrides) =>
            previousOverrides.size === 0 ? previousOverrides : EMPTY_OVERRIDES,
        );
        setShiftOverrides((previousOverrides) =>
            previousOverrides.size === 0
                ? previousOverrides
                : EMPTY_SHIFT_OVERRIDES,
        );
    }, [scopeKey]);

    // Every unconfirmed change as per-shift picks, so the pills and the row
    // checkbox read one projection: a whole-row change covers each of the
    // unit's shifts, and a pill change refines it.
    const pendingPicks = useMemo(() => {
        if (stagedOverrides.size === 0 && shiftOverrides.size === 0) {
            return NO_PENDING_PICKS;
        }
        return [
            ...[...stagedOverrides].flatMap(([caregiverId, selected]) =>
                unitShiftIds.map((shiftId) => ({
                    caregiverId,
                    shiftId,
                    selected,
                })),
            ),
            ...shiftOverrides.values(),
        ];
    }, [shiftOverrides, stagedOverrides, unitShiftIds]);

    // Per-shift staged rows for the whole outreach, keyed by caregiver. The
    // caller intersects these with the group's `shift_ids` from the outreach
    // payload, so coverage never depends on `group_id`, which the server
    // nulls when a group is deleted.
    const serverStagedShiftIds = useMemo(
        () =>
            stagedShiftIdsByCaregiver(
                data?.selections ?? EMPTY_SELECTIONS,
                NO_PENDING_PICKS,
            ),
        [data],
    );
    const selectionCoverageByCaregiverId = useMemo(
        () =>
            pendingPicks.length === 0
                ? serverStagedShiftIds
                : stagedShiftIdsByCaregiver(
                      data?.selections ?? EMPTY_SELECTIONS,
                      pendingPicks,
                  ),
        [data, pendingPicks, serverStagedShiftIds],
    );

    // Reconcile both override levels against every arrival of fresh rows: an
    // override survives only while the server still disagrees with it, so a
    // response that predates the write cannot clear it.
    useEffect(() => {
        setShiftOverrides((previousOverrides) =>
            dropOverrides(
                previousOverrides,
                (_key, { caregiverId, shiftId, selected }) =>
                    (serverStagedShiftIds.get(caregiverId)?.has(shiftId) ??
                        false) === selected,
            ),
        );
    }, [serverStagedShiftIds]);
    useEffect(() => {
        for (const caregiverId of [...locallyStagedIdsRef.current]) {
            if (userStagedCaregiverIds.has(caregiverId)) {
                locallyStagedIdsRef.current.delete(caregiverId);
            }
        }
        setStagedOverrides((previousOverrides) =>
            dropOverrides(
                previousOverrides,
                (caregiverId, isStaged) =>
                    serverStagedCaregiverIds.has(caregiverId) === isStaged,
            ),
        );
    }, [serverStagedCaregiverIds, userStagedCaregiverIds]);

    const stagedCaregiverIds = useMemo(() => {
        if (pendingPicks.length === 0 && stagedOverrides.size === 0) {
            return serverStagedCaregiverIds;
        }
        // A caregiver stays checked while any of this unit's shifts is still
        // staged for them, counting picks not yet confirmed.
        const ids = new Set(
            stagedShiftIdsByCaregiver(unitSelections, pendingPicks).keys(),
        );
        // A unit without known member shifts has no per-shift picks to read
        // a whole-row change from.
        for (const [caregiverId, isStaged] of stagedOverrides) {
            if (isStaged) {
                ids.add(caregiverId);
            } else {
                ids.delete(caregiverId);
            }
        }
        return ids;
    }, [
        pendingPicks,
        serverStagedCaregiverIds,
        stagedOverrides,
        unitSelections,
    ]);

    const persistShiftSelection = useCallback(
        ({
            caregiverId,
            shiftId,
            selected,
        }: {
            readonly caregiverId: string;
            readonly shiftId: string;
            readonly selected: boolean;
        }) => {
            if (
                !organizationId ||
                !outreachId ||
                unitId === null ||
                unitType !== 'shift_group'
            ) {
                return;
            }
            // Caregiver and shift ids are UUIDs, so ':' cannot appear
            // inside either.
            const key = `${caregiverId}:${shiftId}`;
            // The newer action wins: a whole-row change still pending for
            // this caregiver would otherwise outlive the rows it disagrees
            // with and pin the checkbox.
            setStagedOverrides((previousOverrides) =>
                dropOverrides(previousOverrides, (id) => id === caregiverId),
            );
            setShiftOverrides((previousOverrides) =>
                new Map(previousOverrides).set(key, {
                    caregiverId,
                    shiftId,
                    selected,
                }),
            );
            const dropOverride = () =>
                setShiftOverrides((previousOverrides) => {
                    const nextOverrides = new Map(previousOverrides);
                    nextOverrides.delete(key);
                    return nextOverrides;
                });
            void batchUpdateRecommendationCaregiverSelections({
                organizationId,
                outreachId,
                unitType,
                unitId,
                shiftIds: [shiftId],
                items: [
                    {
                        caregiver_id: caregiverId,
                        // Always a withdraw, never a dismiss: a dismissed
                        // row marks the caregiver dismissed for the whole
                        // group, even while other shifts stay picked.
                        action: selected ? 'stage' : 'withdraw',
                    },
                ],
            })
                .then(
                    (response) =>
                        response.items.some((item) => item.error_code !== null),
                    // A transport failure leaves the write unresolved.
                    () => true,
                )
                .then((failed) => {
                    if (failed) {
                        dropOverride();
                        toastSelectionSaveFailed();
                    }
                    void mutate();
                });
        },
        [mutate, organizationId, outreachId, unitId, unitType],
    );

    const persistSelectionDelta = useCallback(
        ({
            addedIds,
            removedIds,
        }: {
            readonly addedIds: ReadonlyArray<string>;
            readonly removedIds: ReadonlyArray<string>;
        }) => {
            if (
                !organizationId ||
                !outreachId ||
                unitId === null ||
                (addedIds.length === 0 && removedIds.length === 0)
            ) {
                return;
            }
            setStagedOverrides((previousOverrides) => {
                const nextOverrides = new Map(previousOverrides);
                for (const caregiverId of addedIds) {
                    nextOverrides.set(caregiverId, true);
                }
                for (const caregiverId of removedIds) {
                    nextOverrides.set(caregiverId, false);
                }
                return nextOverrides;
            });
            // The newer action wins over any pill change still pending for
            // these caregivers.
            const changedIds = new Set([...addedIds, ...removedIds]);
            setShiftOverrides((previousOverrides) =>
                dropOverrides(previousOverrides, (_key, override) =>
                    changedIds.has(override.caregiverId),
                ),
            );

            const writes = [
                ...addedIds.map((caregiverId) => {
                    locallyStagedIdsRef.current.add(caregiverId);
                    return {
                        caregiverId,
                        staged: true,
                        wasLocallyStaged: false,
                        action: 'stage' as const,
                    };
                }),
                ...removedIds.map((caregiverId) => {
                    const wasLocallyStaged =
                        locallyStagedIdsRef.current.has(caregiverId);
                    const stagedByCoordinator =
                        wasLocallyStaged ||
                        userStagedCaregiverIds.has(caregiverId);
                    locallyStagedIdsRef.current.delete(caregiverId);
                    return {
                        caregiverId,
                        staged: false,
                        wasLocallyStaged,
                        action: stagedByCoordinator
                            ? ('withdraw' as const)
                            : ('dismiss' as const),
                    };
                }),
            ];

            void batchUpdateRecommendationCaregiverSelections({
                organizationId,
                outreachId,
                unitType,
                unitId,
                items: writes.map((write) => ({
                    caregiver_id: write.caregiverId,
                    action: write.action,
                })),
            })
                .then(
                    (response) => {
                        const failedIds = new Set(
                            response.items
                                .filter((item) => item.error_code !== null)
                                .map((item) => item.caregiver_id),
                        );
                        return writes.filter((write) =>
                            failedIds.has(write.caregiverId),
                        );
                    },
                    // A transport failure leaves every id unresolved.
                    () => writes,
                )
                .then((failed) => {
                    if (failed.length > 0) {
                        setStagedOverrides((previousOverrides) => {
                            const nextOverrides = new Map(previousOverrides);
                            for (const write of failed) {
                                nextOverrides.delete(write.caregiverId);
                                if (write.staged) {
                                    locallyStagedIdsRef.current.delete(
                                        write.caregiverId,
                                    );
                                } else if (write.wasLocallyStaged) {
                                    // A failed withdraw of a never-persisted
                                    // local stage keeps its coordinator
                                    // ownership; else a retry would dismiss.
                                    locallyStagedIdsRef.current.add(
                                        write.caregiverId,
                                    );
                                }
                            }
                            return nextOverrides;
                        });
                        toastSelectionSaveFailed();
                    }
                    const failedIds = new Set(
                        failed.map((write) => write.caregiverId),
                    );
                    const dismissedIds = writes
                        .filter(
                            (write) =>
                                write.action === 'dismiss' &&
                                !failedIds.has(write.caregiverId),
                        )
                        .map((write) => write.caregiverId);
                    if (dismissedIds.length > 0) {
                        onDismissalsPersisted?.(dismissedIds);
                    }
                    void mutate();
                });
        },
        [
            mutate,
            onDismissalsPersisted,
            organizationId,
            outreachId,
            unitId,
            unitType,
            userStagedCaregiverIds,
        ],
    );

    // A fresh object here would change identity on every render of every
    // table, rebuilding the memoized columns and remounting each header —
    // the WEB-CS update-depth cascade. Every member below is already
    // memoized or a primitive, so this stays stable between real changes.
    const isReady = !enabled || data !== undefined || error !== undefined;
    return useMemo(
        () => ({
            isReady,
            stagedCaregiverIds,
            selectionCoverageByCaregiverId,
            persistSelectionDelta,
            persistShiftSelection,
        }),
        [
            isReady,
            persistSelectionDelta,
            persistShiftSelection,
            selectionCoverageByCaregiverId,
            stagedCaregiverIds,
        ],
    );
}
